import {controlStyle} from '../lib/formStyles.js';
import Button from '../shared/Button.js';
// Sheep detail pane — inline-editable expanded tile, mirrors CowDetail.tsx
// byte-for-byte functionality; only field names (sheep / flock / lambing /
// dam_reg_num / sire_reg_num / ewe-ram-wether) differ. Ronnie's request
// 2026-04-23: sheep flocks tab should match cattle herds tab exactly,
// with only the fields and data distinct.
import React from 'react';
import RuminantWeightHistory from '../cattle/RuminantWeightHistory.js';
import {
  ruminantDetailCardStyle,
  ruminantDetailHeaderRowStyle,
  ruminantSectionHeaderStyle,
  RuminantNavigateBackBanner,
  RuminantPriorTagsSection,
} from '../cattle/ruminantPresentation.tsx';
import {todayCentralISO} from '../lib/dateUtils.js';

import {
  recordFieldRowClass,
  recordFieldLabel,
  recordControl,
  recordTextarea,
  recordCheckbox,
} from '../shared/recordPageControls.jsx';

type SheepRecord = Record<string, any>;
type SheepDetailProps = {
  sheep: SheepRecord;
  weighIns?: SheepRecord[];
  lambing?: SheepRecord[];
  lambs?: SheepRecord[];
  dam?: SheepRecord | null;
  sheepList?: SheepRecord[];
  fmt: (...args: any[]) => any;
  FLOCKS?: string[];
  FLOCK_LABELS: Record<string, string>;
  onEdit?: (...args: any[]) => any;
  onDelete?: (...args: any[]) => any;
  onAddLambing?: (...args: any[]) => any;
  onDeleteLambing?: (...args: any[]) => any;
  onNavigateToSheep?: (sheep: SheepRecord) => void;
  onNavigateBack?: () => void;
  canNavigateBack?: boolean;
  backToTag?: string | null;
  onPatch?: (patch: SheepRecord) => void;
  onTransfer?: (flock: string) => void;
  // Migration 221: transfer_sheep_animal admits admin, management and
  // farm_team only. A locked select still shows the flock but cannot change it
  // (and never falls back to a raw flock patch).
  transferLocked?: boolean;
  // Migration 222: only admin, management and farm_team may write sheep rows
  // directly. readOnly renders every inline editor disabled and hides the
  // prior-tag add/remove controls, so a read-only role is never offered an
  // edit the database refuses (Design Constitution P01).
  readOnly?: boolean;
  onClose?: () => void;
  originOpts?: SheepRecord[];
  breedOpts?: SheepRecord[];
  ageLabel?: string;
};

const SheepDetail = ({
  sheep,
  weighIns,
  lambing,
  lambs,
  // dam / onEdit: still passed by the record page; the extracted weight
  // history no longer needs them here.
  dam: _dam,
  sheepList,
  fmt,
  FLOCKS,
  FLOCK_LABELS,
  onEdit: _onEdit,
  onDelete,
  onAddLambing,
  onDeleteLambing,
  onNavigateToSheep,
  onNavigateBack,
  canNavigateBack,
  backToTag,
  onPatch,
  onTransfer,
  transferLocked = false,
  readOnly = false,
  onClose,
  originOpts,
  breedOpts,
  ageLabel,
}: SheepDetailProps) => {
  const patchOnBlur = (field: string, parser: 'number' | 'text') => (ev: React.FocusEvent<HTMLInputElement>) => {
    if (!onPatch) return;
    const raw = ev.target.value;
    const cur = sheep[field];
    let next;
    if (parser === 'number') {
      next = raw === '' ? null : parseFloat(raw);
      if (Number.isNaN(next)) next = null;
    } else {
      next = (raw || '').trim() || null;
    }
    const curNorm = cur === '' ? null : cur;
    if (String(next) === String(curNorm)) return;
    onPatch({[field]: next});
  };
  const patchOnChange = (field: string) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!onPatch) return;
    const v = ev.target.type === 'checkbox' ? ev.target.checked : ev.target.value || null;
    onPatch({[field]: v});
  };
  // Flock is audit-critical (spans sold/deceased/processed). Route flock changes
  // through the transfer RPC (transfer_sheep_animal — atomic sheep_transfers
  // ledger + status.changed Activity) via onTransfer, never the raw patch —
  // mirrors CowDetail.transferOnChange. Falls back to onPatch only if no
  // onTransfer handler is wired.
  const transferOnChange = (ev: React.ChangeEvent<HTMLSelectElement>) => {
    if (transferLocked) return;
    const v = ev.target.value || null;
    if (!v || v === sheep.flock) return;
    if (typeof onTransfer === 'function') {
      onTransfer(v);
      return;
    }
    if (onPatch) onPatch({flock: v});
  };
  function patchOldTagAt(idx: number, field: string, value: any) {
    if (!onPatch) return;
    const next = (Array.isArray(sheep.old_tags) ? sheep.old_tags : []).slice();
    next[idx] = {...next[idx], [field]: value};
    onPatch({old_tags: next});
  }
  function addOldTag() {
    if (!onPatch) return;
    const next = [
      ...(Array.isArray(sheep.old_tags) ? sheep.old_tags : []),
      {tag: '', changed_at: '', source: 'manual'},
    ];
    onPatch({old_tags: next});
  }
  function removeOldTag(idx: number) {
    if (!onPatch) return;
    const next = (Array.isArray(sheep.old_tags) ? sheep.old_tags : []).filter((_, i) => i !== idx);
    onPatch({old_tags: next});
  }
  const editInp: React.CSSProperties = controlStyle;
  const [showLambForm, setShowLambForm] = React.useState(false);
  const [lambForm, setLambForm] = React.useState({
    lambing_date: todayCentralISO(),
    total_born: '',
    deaths: '',
    complications_flag: false,
    complications_desc: '',
    notes: '',
  });
  const sectionTitle = ruminantSectionHeaderStyle;
  // Ewes only — lambing history is sex-scoped so a retired ewe (now in
  // 'processed'/'sold'/'deceased' flock) still shows her lambing records.
  // Wethers / rams / lambs never see this section.
  const isEwe = sheep.sex === 'ewe';
  async function submitLambing() {
    if (!onAddLambing) return;
    const ok = await onAddLambing(lambForm);
    if (ok) {
      setShowLambForm(false);
      setLambForm({
        lambing_date: todayCentralISO(),
        total_born: '',
        deaths: '',
        complications_flag: false,
        complications_desc: '',
        notes: '',
      });
    }
  }

  const findByTag = (t: string | null | undefined): SheepRecord | null =>
    t && Array.isArray(sheepList) ? sheepList.find((x: SheepRecord) => x.tag === t) || null : null;
  const linkStyle = {
    color: 'var(--info)',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  };
  const TagLink = ({tag, prefix, label}: {tag?: string; prefix?: string; label?: string}) => {
    const t = findByTag(tag);
    if (!t || !onNavigateToSheep) return <span>{(prefix || '') + (label || '#' + tag)}</span>;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onNavigateToSheep(t);
        }}
        style={linkStyle}
      >
        {(prefix || '') + (label || '#' + tag)}
      </button>
    );
  };

  return (
    <div style={ruminantDetailCardStyle}>
      <RuminantNavigateBackBanner
        canNavigateBack={canNavigateBack}
        onNavigateBack={onNavigateBack}
        backToTag={backToTag}
        noun="sheep"
        linkColor="var(--info)"
      />
      {/* Header: editable tag + labeled flock/sex/breed selects + close X */}
      <div style={ruminantDetailHeaderRowStyle}>
        <div style={{display: 'flex', alignItems: 'center', gap: 4}}>
          <span style={{fontSize: 16, fontWeight: 700, color: 'var(--text-primary)'}}>{'#'}</span>
          <input
            type="text"
            defaultValue={sheep.tag || ''}
            disabled={readOnly}
            onBlur={patchOnBlur('tag', 'text')}
            placeholder="tag"
            style={{...editInp, width: 90, fontWeight: 700, fontSize: 14, color: 'var(--text-primary)'}}
          />
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Flock
          </div>
          <select
            data-sheep-flock-status-select="1"
            defaultValue={sheep.flock || ''}
            onChange={transferOnChange}
            disabled={transferLocked}
            style={{...editInp, width: 140}}
          >
            {(FLOCKS || []).map((f: string) => (
              <option key={f} value={f}>
                {FLOCK_LABELS[f] || f}
              </option>
            ))}
          </select>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Sex
          </div>
          <select
            defaultValue={sheep.sex || ''}
            disabled={readOnly}
            onChange={patchOnChange('sex')}
            style={{...editInp, width: 110}}
          >
            <option value="">{'— sex —'}</option>
            <option value="ewe">Ewe</option>
            <option value="ram">Ram</option>
            <option value="wether">Wether</option>
            <option value="lamb">Lamb</option>
          </select>
        </div>
        <div>
          <div
            style={{
              fontSize: 10,
              color: 'var(--ink-muted)',
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            Breed
          </div>
          <select
            defaultValue={sheep.breed || ''}
            disabled={readOnly}
            onChange={patchOnChange('breed')}
            style={{...editInp, width: 210}}
          >
            <option value="">{'— breed —'}</option>
            {(breedOpts || []).map((b: SheepRecord) => (
              <option key={b.id} value={b.label}>
                {b.label}
              </option>
            ))}
            {sheep.breed && !(breedOpts || []).some((b: SheepRecord) => b.label === sheep.breed) && (
              <option value={sheep.breed}>{sheep.breed + ' (historical)'}</option>
            )}
          </select>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close"
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: '1px solid var(--border-strong)',
              borderRadius: 10,
              color: 'var(--ink-muted)',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '2px 10px',
              fontFamily: 'inherit',
            }}
          >
            {'×'}
          </button>
        )}
      </div>

      {/* Identity | Lineage: two columns on desktop; one column on phones (§7) so
          the Lineage inputs never sit off-screen at 390px. */}
      <div data-mobile-1col="1" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16}}>
        {/* Identity (editable) */}
        <div>
          <div style={sectionTitle}>Identity</div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink)',
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '4px 8px',
              alignItems: 'center',
            }}
          >
            <span style={{color: 'var(--ink-faint)'}}>Origin</span>
            <select
              defaultValue={sheep.origin || ''}
              disabled={readOnly}
              onChange={patchOnChange('origin')}
              style={editInp}
            >
              <option value="">{'— select —'}</option>
              {(originOpts || []).map((o: SheepRecord) => (
                <option key={o.id} value={o.label}>
                  {o.label}
                </option>
              ))}
              {sheep.origin && !(originOpts || []).some((o: SheepRecord) => o.label === sheep.origin) && (
                <option value={sheep.origin}>{sheep.origin}</option>
              )}
            </select>
            <span style={{color: 'var(--ink-faint)'}}>Birth</span>
            <input
              type="date"
              defaultValue={sheep.birth_date || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('birth_date', 'text')}
              style={editInp}
            />
            <span style={{color: 'var(--ink-faint)'}}>Age</span>
            <span
              style={{
                fontSize: 12,
                padding: '5px 8px',
                color: 'var(--ink-muted)',
                fontFamily: 'inherit',
              }}
            >
              {ageLabel || '—'}
            </span>
            <span style={{color: 'var(--ink-faint)'}}>Purchased</span>
            <input
              type="date"
              defaultValue={sheep.purchase_date || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('purchase_date', 'text')}
              style={editInp}
            />
            <span style={{color: 'var(--ink-faint)'}}>Purchase $</span>
            <input
              type="number"
              min="0"
              step="0.01"
              defaultValue={sheep.purchase_amount == null ? '' : sheep.purchase_amount}
              disabled={readOnly}
              onBlur={patchOnBlur('purchase_amount', 'number')}
              style={editInp}
            />
            {sheep.sex === 'ewe' && (
              <>
                <span style={{color: 'var(--ink-faint)'}}>Breeding</span>
                <select
                  defaultValue={sheep.breeding_status || ''}
                  disabled={readOnly}
                  onChange={patchOnChange('breeding_status')}
                  style={editInp}
                >
                  <option value="">{'— not set —'}</option>
                  <option value="Open">Open</option>
                  <option value="Pregnant">Pregnant</option>
                  <option value="N/A">N/A</option>
                </select>
              </>
            )}
          </div>
        </div>
        {/* Lineage (editable) */}
        <div>
          <div style={sectionTitle}>Lineage</div>
          <div
            style={{
              fontSize: 12,
              color: 'var(--ink)',
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              gap: '4px 8px',
              alignItems: 'center',
            }}
          >
            <span style={{color: 'var(--ink-faint)'}}>Dam Tag #</span>
            <input
              type="text"
              defaultValue={sheep.dam_tag || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('dam_tag', 'text')}
              style={editInp}
            />
            {sheep.dam_tag && findByTag(sheep.dam_tag) && (
              <>
                <span style={{color: 'var(--ink-faint)'}}>{}</span>
                <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                  <TagLink tag={sheep.dam_tag} prefix="View " />{' '}
                  {'(' + ((findByTag(sheep.dam_tag) || {}).breed || '?') + ')'}
                </span>
              </>
            )}
            <span style={{color: 'var(--ink-faint)'}}>Sire Tag #</span>
            <input
              type="text"
              defaultValue={sheep.sire_tag || ''}
              disabled={readOnly}
              onBlur={patchOnBlur('sire_tag', 'text')}
              style={editInp}
            />
            {sheep.sire_tag && findByTag(sheep.sire_tag) && (
              <>
                <span style={{color: 'var(--ink-faint)'}}>{}</span>
                <span style={{fontSize: 11, color: 'var(--ink-muted)'}}>
                  <TagLink tag={sheep.sire_tag} prefix="View " />{' '}
                  {'(' + ((findByTag(sheep.sire_tag) || {}).breed || '?') + ')'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Prior Tags editor */}
      <RuminantPriorTagsSection
        oldTags={sheep.old_tags || []}
        readOnly={readOnly}
        editInp={editInp}
        onAdd={addOldTag}
        onPatchAt={patchOldTagAt}
        onRemove={removeOldTag}
      />

      {/* Breeding blacklist (hidden for wethers — they can't breed) */}
      {sheep.sex !== 'wether' && (
        <label
          data-breeding-blacklist-row="1"
          style={{
            marginTop: 10,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 8,
            maxWidth: '100%',
            boxSizing: 'border-box',
            padding: '6px 12px',
            // R03: a flagged sheep reads as the danger SOFT tint with danger
            // ink, never a solid fill - the same treatment as CowDetail (A01).
            background: sheep.breeding_blacklist ? 'var(--danger-soft)' : 'var(--surface)',
            color: 'var(--danger-ink)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          <input
            type="checkbox"
            checked={!!sheep.breeding_blacklist}
            disabled={readOnly}
            onChange={patchOnChange('breeding_blacklist')}
            style={{
              margin: 0,
              padding: 0,
              width: 14,
              height: 14,
              minWidth: 14,
              flex: '0 0 14px',
              boxSizing: 'border-box',
              accentColor: 'var(--danger)',
              cursor: 'pointer',
            }}
          />
          <span style={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block'}}>
            Breeding blacklist
          </span>
        </label>
      )}

      {/* Weight history: the shared ruminant section (table / §4 chart, Table D ADG). */}
      {weighIns && weighIns.length > 0 && <RuminantWeightHistory weighIns={weighIns} fmt={fmt} species="sheep" />}

      {/* Lambing history + add (Ewes only) */}
      {isEwe && (
        <div style={{marginTop: 12}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
            <div style={sectionTitle}>Lambing History</div>
            {!showLambForm && onAddLambing && (
              <Button variant="secondary" type="button" onClick={() => setShowLambForm(true)}>
                + Add Lambing
              </Button>
            )}
          </div>
          {showLambForm && (
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 12px',
                marginBottom: 8,
              }}
            >
              <div style={{marginBottom: 6}}>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Lambing date *</span>
                  <input
                    type="date"
                    value={lambForm.lambing_date}
                    onChange={(e) => setLambForm({...lambForm, lambing_date: e.target.value})}
                    style={recordControl}
                  />
                </div>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Total born</span>
                  <input
                    type="number"
                    min="0"
                    value={lambForm.total_born}
                    onChange={(e) => setLambForm({...lambForm, total_born: e.target.value})}
                    placeholder="0"
                    style={recordControl}
                  />
                </div>
                <div className={recordFieldRowClass}>
                  <span style={recordFieldLabel}>Deaths</span>
                  <input
                    type="number"
                    min="0"
                    value={lambForm.deaths}
                    onChange={(e) => setLambForm({...lambForm, deaths: e.target.value})}
                    placeholder="0"
                    style={recordControl}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      color: 'var(--danger-ink)',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={lambForm.complications_flag}
                      onChange={(e) => setLambForm({...lambForm, complications_flag: e.target.checked})}
                      style={recordCheckbox}
                    />
                    Complications flag
                  </label>
                  {lambForm.complications_flag && (
                    <textarea
                      value={lambForm.complications_desc}
                      onChange={(e) => setLambForm({...lambForm, complications_desc: e.target.value})}
                      placeholder="Required: describe complications"
                      rows={2}
                      style={{...recordTextarea, marginTop: 4}}
                    />
                  )}
                </div>
                <div className={recordFieldRowClass} style={{borderBottom: 'none'}}>
                  <span style={recordFieldLabel}>Notes</span>
                  <textarea
                    value={lambForm.notes}
                    onChange={(e) => setLambForm({...lambForm, notes: e.target.value})}
                    rows={2}
                    style={recordTextarea}
                  />
                </div>
              </div>
              <div style={{display: 'flex', gap: 6}}>
                <Button variant="secondary" type="button" onClick={submitLambing}>
                  Save Lambing
                </Button>
                <Button variant="secondary" type="button" onClick={() => setShowLambForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {(() => {
            const combined = [...(lambing || [])].sort((a, b) =>
              (b.lambing_date || '').localeCompare(a.lambing_date || ''),
            );
            if (combined.length === 0) {
              if (showLambForm) return null;
              return (
                <div style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic'}}>
                  No lambing records yet.
                </div>
              );
            }
            return combined.map((r) => (
              <div
                key={r.id}
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '6px 10px',
                  marginBottom: 4,
                  fontSize: 11,
                  display: 'flex',
                  gap: 10,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <strong style={{color: 'var(--ink)'}}>{r.lambing_date ? fmt(r.lambing_date) : 'date unknown'}</strong>
                <span>{(r.total_born || 0) + ' born, ' + (r.deaths || 0) + ' died'}</span>
                {r.complications_flag && (
                  <span style={{color: 'var(--danger-ink)', fontWeight: 600}}>{'⚠ complications'}</span>
                )}
                {r.notes && <span style={{color: 'var(--ink-muted)', fontStyle: 'italic'}}>{r.notes}</span>}
                {onDeleteLambing && (
                  <Button
                    variant="danger"
                    type="button"
                    onClick={() => onDeleteLambing(r.id)}
                    style={{marginLeft: 'auto'}}
                  >
                    Delete
                  </Button>
                )}
              </div>
            ));
          })()}

          {/* Lambs linked to this dam by dam_tag */}
          {lambs && lambs.length > 0 && (
            <div style={{marginTop: 8}}>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--ink-muted)',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                Lambs in Directory ({lambs.length})
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                {lambs.map((l) => (
                  <button
                    type="button"
                    key={l.id}
                    onClick={() => onNavigateToSheep && onNavigateToSheep(l)}
                    style={{
                      fontSize: 11,
                      padding: '3px 8px',
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      background: 'var(--surface)',
                      color: 'var(--info)',
                      cursor: onNavigateToSheep ? 'pointer' : 'default',
                      fontFamily: 'inherit',
                    }}
                  >
                    {'#' + l.tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action buttons (Edit removed — fields are inline-editable above) */}
      <div
        style={{
          marginTop: 12,
          display: 'flex',
          gap: 6,
          flexWrap: 'wrap',
          borderTop: '1px solid var(--border)',
          paddingTop: 10,
        }}
      >
        {onDelete && (
          <Button variant="danger" onClick={onDelete} style={{marginLeft: 'auto'}}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
};

export default SheepDetail;
